import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { Plus, Edit2, Trash2, X, Clock } from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const initialForm = {
  name: '', start_time: '09:00', end_time: '18:00',
  weekly_off: ['Sun'], grace_minutes: 15, is_night: false, color: '#1F7A6E',
};

export default function Shifts() {
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roster, setRoster] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRoster, setShowRoster] = useState(false);
  const [rosterForm, setRosterForm] = useState({ employee_id: '', shift_id: '', from_date: '', to_date: '' });
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    try {
      const [shiftsRes, empRes, rosterRes] = await Promise.all([
        apiGet('/hr/shifts'),
        apiGet('/hr/employees'),
        apiGet('/hr/roster'),
      ]);
      setShifts(shiftsRes.shifts || []);
      setEmployees(empRes);
      setRoster(rosterRes.roster || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);

  const submitShift = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await apiPut(`/hr/shifts/${editing.id}`, form);
        toast.success('Shift updated');
      } else {
        await apiPost('/hr/shifts', form);
        toast.success('Shift created');
      }
      setShowForm(false); setEditing(null); setForm(initialForm); load();
    } catch (err) { toast.error('Failed'); }
  };

  const removeShift = async (id) => {
    if (!window.confirm('Delete shift?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/hr/shifts/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  const toggleOff = (day) => {
    const off = form.weekly_off || [];
    setForm({ ...form, weekly_off: off.includes(day) ? off.filter(d => d !== day) : [...off, day] });
  };

  const submitRoster = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/hr/roster', rosterForm);
      toast.success('Roster assigned');
      setShowRoster(false);
      setRosterForm({ employee_id: '', shift_id: '', from_date: '', to_date: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeRoster = async (id) => {
    if (!window.confirm('Delete roster entry?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/hr/roster/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Shifts & Roster</h1>
          <p className="text-sm text-slate-600 mt-1">Define shift master timings & assign employees to shifts for site/project schedules.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowRoster(true)} className="btn-secondary flex items-center gap-2" data-testid="assign-roster-btn">
            <Plus size={14} /> Assign Roster
          </button>
          <button onClick={() => { setEditing(null); setForm(initialForm); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-shift-btn">
            <Plus size={16} /> New Shift
          </button>
        </div>
      </div>

      {/* Shifts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {shifts.map(s => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-sm p-5 relative" data-testid={`shift-card-${s.id}`}>
            <div className="w-8 h-8 rounded-sm mb-3 flex items-center justify-center" style={{ background: s.color || '#1F7A6E' }}>
              <Clock size={15} className="text-white" />
            </div>
            <div className="font-display font-bold text-base text-[#0F172A]">{s.name}</div>
            <div className="text-xs text-slate-500 mt-1">{s.start_time} → {s.end_time}{s.is_night && ' · Night'}</div>
            <div className="text-xs text-slate-500 mt-1">Grace: {s.grace_minutes} min</div>
            <div className="text-xs text-slate-500 mt-1">Off: {(s.weekly_off || []).join(', ') || '—'}</div>
            <div className="absolute top-3 right-3 flex gap-2">
              <button onClick={() => { setEditing(s); setForm({ ...initialForm, ...s }); setShowForm(true); }} className="text-slate-500 hover:text-[#1F7A6E]"><Edit2 size={14} /></button>
              <button onClick={() => removeShift(s.id)} className="text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!shifts.length && <div className="col-span-full p-8 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-sm">No shifts defined.</div>}
      </div>

      {/* Roster table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="font-display font-bold text-lg">Active Roster Assignments</h3>
        </div>
        <table className="w-full dense-table">
          <thead><tr><th>Employee</th><th>Shift</th><th>From</th><th>To</th><th></th></tr></thead>
          <tbody>
            {roster.map(r => {
              const emp = employees.find(e => e.id === r.employee_id);
              return (
                <tr key={r.id}>
                  <td>
                    <div className="font-display font-semibold text-[#0F172A]">{emp?.name || '—'}</div>
                    <div className="text-xs text-slate-500">{emp?.emp_id}</div>
                  </td>
                  <td className="text-xs">{r.shift_name}</td>
                  <td className="text-xs">{r.from_date}</td>
                  <td className="text-xs">{r.to_date}</td>
                  <td><button onClick={() => removeRoster(r.id)} className="text-red-600 hover:text-red-700"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
            {!roster.length && <tr><td colSpan="5" className="p-6 text-center text-sm text-slate-500">No active assignments.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Shift modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editing ? 'Edit Shift' : 'New Shift'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submitShift} className="p-5 space-y-4">
              <div>
                <label className="label">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g., Day (HQ)" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Start</label><input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="input" /></div>
                <div><label className="label">End</label><input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input" /></div>
                <div><label className="label">Grace (min)</label><input type="number" value={form.grace_minutes} onChange={(e) => setForm({ ...form, grace_minutes: parseInt(e.target.value) })} className="input" /></div>
                <div><label className="label">Color</label><input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="input h-10" /></div>
              </div>
              <div>
                <label className="label">Weekly Off</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {WEEKDAYS.map(d => (
                    <button key={d} type="button" onClick={() => toggleOff(d)} className={`px-3 py-1 text-xs rounded-sm border transition-colors ${(form.weekly_off || []).includes(d) ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 text-slate-600 hover:border-[#1F7A6E]'}`}>{d}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_night} onChange={(e) => setForm({ ...form, is_night: e.target.checked })} />
                Night shift (crosses midnight)
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roster modal */}
      {showRoster && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Assign Roster</h2>
              <button onClick={() => setShowRoster(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submitRoster} className="p-5 space-y-4">
              <div>
                <label className="label">Employee *</label>
                <select value={rosterForm.employee_id} onChange={(e) => setRosterForm({ ...rosterForm, employee_id: e.target.value })} className="input" required>
                  <option value="">Select employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} — ${e.name}`}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Shift *</label>
                <select value={rosterForm.shift_id} onChange={(e) => setRosterForm({ ...rosterForm, shift_id: e.target.value })} className="input" required>
                  <option value="">Select shift</option>
                  {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">From *</label><input type="date" value={rosterForm.from_date} onChange={(e) => setRosterForm({ ...rosterForm, from_date: e.target.value })} className="input" required /></div>
                <div><label className="label">To *</label><input type="date" value={rosterForm.to_date} onChange={(e) => setRosterForm({ ...rosterForm, to_date: e.target.value })} className="input" required /></div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowRoster(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
